import type { SecurityCitationCreateBody } from 'services/captable';
import type { CitationReferenceData, TokenReference } from 'services/reference-generator/types';
import { citationReviewStatus, isAiCreatedCitation } from './reviewAggregator';

/**
 * SCAL-8970 - the manual-citation add decision, extracted pure.
 *
 * DEFAULT = APPEND (keep both citations). Replace is a deliberate, explicit
 * user choice that soft-dismisses the cell's AI citation; it
 * must never happen implicitly, so the drawer only offers it when
 * `shouldOfferReplace` says the choice is real.
 */

export type ManualCitationMode = 'append' | 'replace';

export interface ManualCitationTarget {
  fieldName: string | undefined;
  documentReferenceId: number;
  tokenReference: TokenReference;
}

// The field's visible same-document citation a new selection joins; a dismissed one is skipped, so the create
// request carries the new selection alone.
const sameDocEntry = (refs: CitationReferenceData[], fieldName: string | undefined, documentReferenceId: number) =>
  refs.find(
    ref =>
      ref.field_name === fieldName &&
      ref.document_reference === documentReferenceId &&
      citationReviewStatus(ref) !== 'dismissed'
  );

/**
 * Whether the add should prompt for Append vs Replace: only when the pushed
 * citation would CREATE a new entry (no same-document entry to merge into) and
 * the field already carries a visible (non-dismissed) AI-created citation.
 */
export const shouldOfferReplace = (
  existingRefs: CitationReferenceData[] | undefined,
  fieldName: string | undefined,
  documentReferenceId: number
): boolean => {
  const refs = existingRefs ?? [];
  return (
    !sameDocEntry(refs, fieldName, documentReferenceId) &&
    refs.some(
      ref =>
        ref.field_name === fieldName && isAiCreatedCitation(ref) && citationReviewStatus(ref) !== 'dismissed'
    )
  );
};

/**
 * Add a pushed citation to an unsaved column's cell, for Save to create with the new security: merge the token into
 * the field's live same-document entry, otherwise append a new human-created entry. Replace dismisses the field's
 * AI citations locally so Save copies only the accepted evidence, without changing the source column's references.
 */
export const addManualCitation = (
  existingRefs: CitationReferenceData[] | undefined,
  { fieldName, documentReferenceId, tokenReference }: ManualCitationTarget,
  mode: ManualCitationMode
): CitationReferenceData[] => {
  const refs = existingRefs ?? [];
  const existing = sameDocEntry(refs, fieldName, documentReferenceId);
  if (existing) {
    // The entry now carries the analyst's selection, so it is an accepted citation rather than a pending suggestion.
    existing.tokenReferences.push(tokenReference);
    existing.is_accepted = true;
    existing.status = 'accepted';
    return refs;
  }
  return [
    ...refs.map(ref =>
      mode === 'replace' && ref.field_name === fieldName && isAiCreatedCitation(ref)
        ? { ...ref, status: 'dismissed' as const, is_accepted: false }
        : ref
    ),
    {
      document_reference: documentReferenceId,
      field_name: fieldName ?? '',
      is_accepted: true,
      created_source: 'human',
      tokenReferences: [tokenReference],
    },
  ];
};

/**
 * The create request for a manual citation on a saved security: every selection the citation ends with
 * (a live same-document citation's plus the new one, minus items without token ids or a text, which the
 * endpoint refuses) and the Append / Replace choice.
 */
export const manualCitationRequest = (
  existingRefs: CitationReferenceData[] | undefined,
  { fieldName, documentReferenceId, tokenReference }: ManualCitationTarget,
  mode: ManualCitationMode
): SecurityCitationCreateBody => ({
  field_name: fieldName ?? '',
  document_reference: documentReferenceId,
  token_references: [
    ...(sameDocEntry(existingRefs ?? [], fieldName, documentReferenceId)?.tokenReferences ?? []),
    tokenReference,
  ].filter(ref => ref.token_ids?.length && typeof ref.plain_text === 'string'),
  mode,
});
